import React from 'react';
import UserInfo from './UserInfo';
import { Link, matchPath, useLocation, withRouter } from 'react-router-dom';
import { LoggerFactory } from 'logger';
import { ReactComponent as EquinixLogoOnly } from 'static/img/wb-only-logo.svg';
import { ReactComponent as EquinixLogo } from 'static/img/equinix-logo.svg';
import { ReactComponent as EquinixTextLogo } from 'static/img/wb-text-logo.svg';
import { Actions } from 'stores/UserStore';
import Config from 'Config';
import './Header.scss';
import cn from 'classnames';
import { useMutation } from 'react-apollo';
import { Mutations } from 'gql';
import { TEMPLATE_LIST_VIEW_SELECTION, PROJECT_LIST_VIEW_SELECTION } from 'views/ProjectListView/DefaultListViewSelection';
import { useListView } from 'providers/ListViewProvider';
import useLiteHeaderState from 'views/LiteView/useLiteHeaderState';
import { toast } from 'utils/Toast';

const Header = ({ user, project, history, isTemplate, template }) => {
  const { setListView } = useListView();
  const logger = LoggerFactory.getLogger('Header');
  const [execSignOut, { error }] = useMutation(Mutations.SIGN_OUT);
  error && logger.error(`Error in Signout mutation: ${error}`);

  const location = useLocation();
  const isLogin = location.pathname === '/';
  const isLite = location.pathname === '/connectivity-advisor';
  const liteHeader = useLiteHeaderState();
  const isLiteProject = !!matchPath(location.pathname, { path: '/connectivity-advisor/:projectId', exact: true });

  const onLogoutClicked = async () => {
    logger.debug(`Logging out user: ${user}`);

    await execSignOut();

    Actions.logout();

    window.$sleek.shutdown();

    logger.debug('Redirecting to login view');

    history.replace('/');
  };

  const setView = () => {
    const linkState = isTemplate ? TEMPLATE_LIST_VIEW_SELECTION : PROJECT_LIST_VIEW_SELECTION;
    setListView(linkState);
  };

  const copyCurrentLink = async () => {
    const currentUrl = window.location.href;
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(currentUrl);
    } else {
      const input = document.createElement('input');
      input.value = currentUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    toast('Link copied to clipboard', 'info');
  };

  if (isLite)
    return (
      <header className="lite-top-bar">
        <div className="lite-top-bar__inner">
          <a href="/connectivity-advisor" className="lite-brand">
            <EquinixLogo />
          </a>
          <div className="progress-meter">
            <div className="progress-meter">{liteHeader.stepLabel}</div>

            <div className="progress-meter__track">
              <span style={{ width: `${liteHeader.progressPercent}%` }} />
            </div>
            {liteHeader.totalSteps > 1 && (
              <div className="progress-meter">
                {liteHeader.stepNumber} of {liteHeader.totalSteps}
              </div>
            )}
          </div>
        </div>
      </header>
    );
  if (isLiteProject)
    return (
      <header className={cn('map-header', { 'project-detail': !!project, 'login-page': isLogin })}>
        <div className={'top-header'}>
          <div className={'top-header-start'}>
            <a href="/" className={'logo'} data-testid={'logo'}>
              <EquinixLogo />
            </a>
          </div>
          <div className="user">
            {/* <button className="user_menu" data-badge-changelog onClick={() => {}}>
              Print + Save PDF
            </button>
            <p className={'slash-separator'}>·</p> */}

            <button className="user_menu" data-badge-changelog onClick={copyCurrentLink}>
              Share
            </button>
          </div>
        </div>
      </header>
    );
  else
    return (
      <header className={cn('map-header', { 'project-detail': !!project, 'login-page': isLogin })}>
        <div className={'top-header'}>
          <div className={'top-header-start'}>
            <a href="/" className={'logo'} data-testid={'logo'}>
              {isLogin ? <EquinixTextLogo /> : <EquinixLogoOnly />}
            </a>
            {project && (
              <div className={'project-meta-data'}>
                <Link to={{ pathname: '/projects' }} onClick={setView}>
                  {isTemplate ? 'All templates' : 'All projects'}
                </Link>
                <p className={'slash-separator'}>/</p>
                <p className={'project-and-customer-name'} data-testid={'project-name'}>
                  {template ? template.name : `${project.customer?.name} • ${project.name}`}
                </p>
              </div>
            )}
          </div>
          {user && <UserInfo user={user} version={Config.VERSION} project={project} isTemplate={isTemplate} onLogoutClicked={onLogoutClicked} />}
        </div>
      </header>
    );
};

export default withRouter(Header);
